import { ConnectorFormDetails, Input, Label } from "@simplicity/ui";
import { TablesEditor, type Schedules, type TableConfig } from "../tables";
import type { ConnectorFormProps } from "./GenericVarsForm";

/**
 * File-drop connector form. Unlike IReadySftpForm, the SFTP connection fields
 * (host, port, username, sftpWrapperUrl) live nested under `vars.acquisition`
 * (a discriminated union keyed on `mode`). `tables`, `schedules`, and dataset
 * overrides remain flat on `vars`.
 */

type UpdateFn = (key: string, value: string | number) => void;

interface Acquisition {
  mode: "remote-sftp";
  host?: string;
  port?: number;
  username?: string;
  sftpWrapperUrl?: string;
}

function TextField(props: {
  id: string;
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
}) {
  return (
    <div className="flex-1">
      <Label htmlFor={props.id} className="form-label">
        {props.label}
      </Label>
      <Input
        id={props.id}
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="font-mono"
      />
    </div>
  );
}

function ConnectionFields(props: {
  host: string;
  port: number | "";
  sftpWrapperUrl: string;
  updateField: UpdateFn;
}) {
  return (
    <>
      <div className="flex gap-4">
        <TextField
          id="fd-host"
          label="Host"
          value={props.host}
          onChange={(v) => props.updateField("host", v)}
          placeholder="Required — SFTP host"
        />
        <div className="flex-1">
          <Label htmlFor="fd-port" className="form-label">
            Port
          </Label>
          <Input
            id="fd-port"
            type="number"
            value={props.port}
            onChange={(e) => {
              const val = e.target.value;
              props.updateField("port", val ? Number(val) : "");
            }}
            placeholder="Default: 22"
            className="font-mono"
          />
        </div>
      </div>
      <TextField
        id="fd-sftp-wrapper-url"
        label="SFTP Wrapper URL"
        value={props.sftpWrapperUrl}
        onChange={(v) => props.updateField("sftpWrapperUrl", v)}
        placeholder="Optional — Cloud Function URL override"
      />
    </>
  );
}

/** Coerce the nested acquisition object out of vars, defaulting to a bare remote-sftp shell. */
function readAcquisition(vars: Record<string, unknown>): Acquisition {
  const raw = vars.acquisition;
  const obj =
    raw != null && typeof raw === "object" && !Array.isArray(raw)
      ? (raw as Record<string, unknown>)
      : {};
  return { ...obj, mode: "remote-sftp" };
}

/** Extract typed field values (both nested acquisition + flat vars) for a simple render body. */
function readFields(vars: Record<string, unknown>) {
  const a = readAcquisition(vars);
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const tables = Array.isArray(vars.tables) ? (vars.tables as TableConfig[]) : [];
  const schedules = (
    vars.schedules != null && typeof vars.schedules === "object" && !Array.isArray(vars.schedules)
      ? vars.schedules
      : {}
  ) as Schedules;
  return {
    acquisition: a,
    tables,
    schedules,
    host: str(a.host),
    port: typeof a.port === "number" ? a.port : ("" as number | ""),
    username: str(a.username),
    sftpWrapperUrl: str(a.sftpWrapperUrl),
    bronzeDataset: str(vars.bronzeDataset),
    silverDataset: str(vars.silverDataset),
  };
}

export function FileDropForm(props: ConnectorFormProps) {
  const f = readFields(props.vars);

  /** Update a nested acquisition field, always preserving `mode: "remote-sftp"`. */
  function updateAcquisition(key: string, value: string | number) {
    const next: Record<string, unknown> = { ...f.acquisition };
    if (value === "" || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    next.mode = "remote-sftp";
    props.onVarsChange({ ...props.vars, acquisition: next });
    props.clearBanner();
  }

  /** Update a flat vars field (dataset overrides). */
  function updateVar(key: string, value: string) {
    const next = { ...props.vars };
    if (value === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    props.onVarsChange(next);
    props.clearBanner();
  }

  return (
    <>
      <div className="flex gap-4">
        <TextField
          id="fd-username"
          label="Username"
          value={f.username}
          onChange={(v) => updateAcquisition("username", v)}
          placeholder="Required — SFTP username"
        />
      </div>
      <TablesEditor
        tables={f.tables}
        schedules={f.schedules}
        onChange={(change) => {
          props.onVarsChange({ ...props.vars, ...change });
          props.clearBanner();
        }}
        connectorType={props.connectorType}
        connectorId={props.connectorId}
      />
      {!props.hideAdvanced && (
        <ConnectorFormDetails
          summary="Advanced Options"
          className="mt-4 rounded border border-border/50"
        >
          <ConnectionFields
            host={f.host}
            port={f.port}
            sftpWrapperUrl={f.sftpWrapperUrl}
            updateField={updateAcquisition}
          />
          <div className="flex gap-4">
            <TextField
              id="fd-bronze-dataset"
              label="Bronze Dataset"
              value={f.bronzeDataset}
              onChange={(v) => updateVar("bronzeDataset", v)}
              placeholder={`Default: bronze__${props.connectorPrefix}`}
            />
            <TextField
              id="fd-silver-dataset"
              label="Silver Dataset"
              value={f.silverDataset}
              onChange={(v) => updateVar("silverDataset", v)}
              placeholder={`Default: silver__${props.connectorPrefix}`}
            />
          </div>
        </ConnectorFormDetails>
      )}
    </>
  );
}
